import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { map, Observable, tap } from 'rxjs';
import {
  FreedomPayResponse,
  RefundResponse,
  RefundTransactionRequest,
  TransactionFilter,
  TransactionList
} from 'src/app/models';
import { ApiType } from 'src/app/pos.config';
import { ErrorService } from 'src/app/services/error/error.service';
import { BaseService } from '../base.service';

@Injectable({
  providedIn: 'root'
})
export class TransactionLookupService extends BaseService {
  constructor(
    store: Store,
    private http: HttpClient,
    private readonly errorService: ErrorService
  ) {
    super(store);
  }

  getTransactionUrl(id?) {
    const url = 'transactions';
    if (id) {
      return `${url}/${id}`;
    }
    return url;
  }

  getTransactions(
    params: Partial<TransactionFilter>
  ): Observable<TransactionList[]> {
    return this.http.get<TransactionList[]>(this.getTransactionUrl(), {
      params
    });
  }

  getMealPlanTransactions(
    params: Partial<TransactionFilter>
  ): Observable<TransactionList[]> {
    return this.http
      .get<{
        success: boolean;
        transactions: TransactionList[];
      }>('mealplan_transactions', { params })
      .pipe(
        map((response) => (response?.success ? response.transactions : []))
      );
  }

  refund(
    ticket_uuid: string,
    pos_operator_id: number,
    ticket_items_uuid: string[]
  ) {
    let requestBody: any = { pos_operator_id };
    if (ticket_items_uuid?.length) {
      requestBody = {
        ...requestBody,
        individual_ticket_item_ids: ticket_items_uuid.join(',')
      };
    }

    return this.http
      .post<RefundResponse>(
        this.getLocationUrl(`tickets/${ticket_uuid}/refund`),
        null,
        { params: requestBody }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        tap((response) => console.log('ddevbug refund response: ', response))
      );
  }

  addRefundTransaction(
    ticket_uuid: string,
    refund_request: RefundTransactionRequest,
    pos_operator_id: number,
    terminalResponse?: FreedomPayResponse,
    original_gateway_transaction_id?: number
  ) {
    let params: any = { ...refund_request, pos_operator_id };

    // Handle FreedomPay terminal response if provided
    if (terminalResponse) {
      params = _.omitBy(
        {
          ...params,
          type: 'Pos::Transaction::CreditFreedomPay',
          freedompay_approval_code: terminalResponse.ApprovalCode,
          freedompay_approved_amount: terminalResponse.ApprovedAmount,
          freedompay_dcc_accepted: terminalResponse.DCCAccepted || false,
          freedompay_decision: terminalResponse.Decision,
          freedompay_device_verified: terminalResponse.DeviceVerified || false,
          freedompay_error_code:
            terminalResponse.ErrorCode ||
            terminalResponse.ProcessorResponseCode,
          freedompay_masked_card_number: terminalResponse.MaskedCardNumber,
          freedompay_merchant_reference_code:
            terminalResponse.MerchantReferenceCode,
          freedompay_message:
            terminalResponse.Message ||
            terminalResponse.ProcessorResponseMessage,
          freedompay_network: terminalResponse.Network || 'FREEWAY',
          freedompay_pin_verified: terminalResponse.PinVerified || false,
          freedompay_processor_response_code:
            terminalResponse.ProcessorResponseCode ||
            terminalResponse.ErrorCode,
          freedompay_processor_response_message:
            terminalResponse.ProcessorResponseMessage ||
            terminalResponse.Message,
          freedompay_request_guid: terminalResponse.RequestGuid,
          freedompay_request_id: terminalResponse.RequestId,
          freedompay_transaction_id: terminalResponse.TransactionId,
          original_gateway_transaction_id
        },
        _.isNil
      );
    }
    return this.http
      .post<RefundResponse>(
        this.getLocationUrl(`tickets/${ticket_uuid}/add_refund_transaction`),
        null,
        { params }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        tap((response) =>
          console.log('ddevbug addRefundTransaction response: ', response)
        )
      );
  }
}
